import React, { useMemo, useState, useEffect, useCallback, createContext, useContext } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useConfig } from './contexts/ConfigContext';
import type { PaletaConfig } from './contexts/ConfigContext';
import baseTheme from './theme';

const DEFAULT_PRIMARY = '#00695C';
const DEFAULT_PRIMARY_LIGHT = '#439889';
const DEFAULT_PRIMARY_DARK = '#004D40';
const DEFAULT_SECONDARY = '#0288D1';
const DEFAULT_SECONDARY_LIGHT = '#5EB8FF';
const DEFAULT_SECONDARY_DARK = '#01579B';
const DEFAULT_BG = '#F0F7F5';
const DEFAULT_PAPER = '#FFFFFF';

const COLOR_MODE_KEY = 'laboratorio_color_mode';

type ColorMode = 'light' | 'dark';

interface ColorModeContextValue {
  mode: ColorMode;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: 'light',
  toggleColorMode: () => undefined,
});

export function useColorMode(): ColorModeContextValue {
  return useContext(ColorModeContext);
}

export function createThemeFromPaleta(
  paleta?: PaletaConfig | null,
  mode: ColorMode = 'light'
) {
  const p = paleta && typeof paleta === 'object' ? paleta : {};
  const primaryMain = p.primaryMain ?? DEFAULT_PRIMARY;
  const primaryLight = p.primaryLight ?? DEFAULT_PRIMARY_LIGHT;
  const primaryDark = p.primaryDark ?? DEFAULT_PRIMARY_DARK;
  const secondaryMain = p.secondaryMain ?? DEFAULT_SECONDARY;
  const secondaryLight = p.secondaryLight ?? DEFAULT_SECONDARY_LIGHT;
  const secondaryDark = p.secondaryDark ?? DEFAULT_SECONDARY_DARK;
  const bgDefault = mode === 'dark' ? '#0F1A18' : (p.backgroundDefault ?? DEFAULT_BG);
  const bgPaper = mode === 'dark' ? '#1A2B28' : (p.backgroundPaper ?? DEFAULT_PAPER);

  return createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      mode,
      primary: {
        main: primaryMain,
        light: primaryLight,
        dark: primaryDark,
        contrastText: '#ffffff',
      },
      secondary: {
        main: secondaryMain,
        light: secondaryLight,
        dark: secondaryDark,
        contrastText: '#ffffff',
      },
      background: {
        default: bgDefault,
        paper: bgPaper,
      },
      ...(mode === 'dark'
        ? {
            text: {
              primary: 'rgba(255,255,255,0.92)',
              secondary: 'rgba(255,255,255,0.68)',
            },
          }
        : {}),
    },
    typography: {
      ...baseTheme.typography,
      h4: { ...baseTheme.typography.h4, color: mode === 'dark' ? primaryLight : primaryDark },
      h5: { ...baseTheme.typography.h5, color: mode === 'dark' ? primaryLight : primaryMain },
    },
    components: {
      ...baseTheme.components,
      MuiCssBaseline: {
        styleOverrides: {
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
          html: {
            colorScheme: mode,
          },
          body: {
            backgroundColor: bgDefault,
            color: mode === 'dark' ? 'rgba(255,255,255,0.92)' : undefined,
          },
          '::selection': {
            backgroundColor: `${primaryMain}40`,
          },
          // Autofill Chrome: no tapar texto/bordes en oscuro
          'input:-webkit-autofill, textarea:-webkit-autofill, select:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px ${bgPaper} inset`,
            WebkitTextFillColor: mode === 'dark' ? 'rgba(255,255,255,0.92)' : 'inherit',
            caretColor: mode === 'dark' ? 'rgba(255,255,255,0.92)' : 'inherit',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? 'rgba(255,255,255,0.72)' : undefined,
            '&.Mui-focused': {
              color: mode === 'dark' ? primaryLight : primaryMain,
            },
            '&.Mui-error': {
              color: undefined,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.04)' : undefined,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.28)' : undefined,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.45)' : undefined,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? primaryLight : primaryMain,
            },
            '& input, & textarea': {
              color: mode === 'dark' ? 'rgba(255,255,255,0.92)' : undefined,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : undefined,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : undefined,
          },
          head: {
            color: mode === 'dark' ? 'rgba(255,255,255,0.92)' : undefined,
          },
        },
      },
      MuiButton: {
        ...baseTheme.components?.MuiButton,
        styleOverrides: {
          ...baseTheme.components?.MuiButton?.styleOverrides,
          root: {
            minHeight: 40,
          },
          contained: {
            boxShadow: `0 2px 8px ${primaryMain}40`,
            '&:hover': {
              boxShadow: `0 4px 12px ${primaryMain}59`,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            // Mejora touch / teclado sin forzar todos los size="small"
            '&.MuiIconButton-sizeMedium': {
              minWidth: 40,
              minHeight: 40,
            },
          },
        },
      },
      MuiPaper: {
        ...baseTheme.components?.MuiPaper,
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.45)' : `0 2px 12px ${primaryDark}14`,
          },
        },
      },
      MuiCard: {
        ...baseTheme.components?.MuiCard,
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.45)' : `0 2px 12px ${primaryDark}14`,
          },
        },
      },
      MuiAppBar: {
        ...baseTheme.components?.MuiAppBar,
        styleOverrides: {
          root: {
            boxShadow: `0 2px 12px ${primaryDark}26`,
          },
        },
      },
      MuiListItemButton: {
        ...baseTheme.components?.MuiListItemButton,
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 8px',
            '&.Mui-selected': {
              backgroundColor: `${primaryMain}1F`,
              '&:hover': {
                backgroundColor: `${primaryMain}2E`,
              },
            },
          },
        },
      },
      MuiTableHead: {
        ...baseTheme.components?.MuiTableHead,
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 700,
              backgroundColor: `${primaryMain}0F`,
              color: mode === 'dark' ? primaryLight : primaryDark,
            },
          },
        },
      },
      MuiTextField: {
        ...baseTheme.components?.MuiTextField,
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              backgroundColor: mode === 'dark' ? 'transparent' : bgPaper,
            },
          },
        },
      },
    },
  });
}

/** Valores por defecto de la paleta (para restaurar en Debug) */
export const DEFAULT_PALETA: PaletaConfig = {
  primaryMain: DEFAULT_PRIMARY,
  primaryLight: DEFAULT_PRIMARY_LIGHT,
  primaryDark: DEFAULT_PRIMARY_DARK,
  secondaryMain: DEFAULT_SECONDARY,
  secondaryLight: DEFAULT_SECONDARY_LIGHT,
  secondaryDark: DEFAULT_SECONDARY_DARK,
  backgroundDefault: DEFAULT_BG,
  backgroundPaper: DEFAULT_PAPER,
  gradientLogin: '135deg, #00695C 0%, #004D40 40%, #0288D1 100%',
  gradientCard: '135deg, rgba(0, 105, 92, 0.08) 0%, rgba(2, 136, 209, 0.06) 100%',
  gradientSidebar: '90deg, #00695C 0%, #004D40 70%, #0288D1 100%',
  gradientPageBg: '160deg, #F0F7F5 0%, #E8F5E9 50%, #E3F2FD 100%',
  printHeaderColor: '#00695c',
  printTableHeaderColor: '#667eea',
  printAccentColor: '#00695c',
};

export function getGradientCss(paleta: PaletaConfig | undefined | null, key: 'gradientLogin' | 'gradientCard' | 'gradientSidebar' | 'gradientPageBg'): string {
  const d = DEFAULT_PALETA[key];
  const v = paleta && paleta[key];
  if (!v || typeof v !== 'string') return `linear-gradient(${d})`;
  return v.startsWith('linear-gradient') ? v : `linear-gradient(${v})`;
}

export function getPrintColor(paleta: PaletaConfig | undefined | null, key: 'printHeaderColor' | 'printTableHeaderColor' | 'printAccentColor'): string {
  const d = DEFAULT_PALETA[key];
  const v = paleta && paleta[key];
  return (v && typeof v === 'string') ? v : (d || '#00695c');
}

interface ThemeFromConfigProps {
  children: React.ReactNode;
}

/** Provee el tema MUI según config.paleta + modo claro/oscuro. Debe usarse dentro de ConfigProvider. */
export function ThemeFromConfig({ children }: ThemeFromConfigProps) {
  const { config } = useConfig();
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setMode] = useState<ColorMode>(() => {
    try {
      const saved = localStorage.getItem(COLOR_MODE_KEY);
      if (saved === 'light' || saved === 'dark') return saved;
    } catch {
      /* ignore */
    }
    return 'light';
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(COLOR_MODE_KEY);
      if (!saved && prefersDark) setMode('dark');
    } catch {
      /* ignore */
    }
  }, [prefersDark]);

  const toggleColorMode = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(COLOR_MODE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const paleta = config?.paleta;
  const theme = useMemo(() => createThemeFromPaleta(paleta, mode), [paleta, mode]);
  const colorModeValue = useMemo(() => ({ mode, toggleColorMode }), [mode, toggleColorMode]);

  return (
    <ColorModeContext.Provider value={colorModeValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
